import { useEffect, useState, useRef } from "react";
import { Progress } from "@/components/ui/progress";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Loader2 } from "lucide-react";
import { cn } from "@/lib/utils";
import { debugLog, debugWarn, debugError } from "@/contexts/DebugContext";

interface SearchProgressProps {
  oem: string;
  onComplete: (results: any[]) => void;
  onError: (error: string) => void;
  onProgressUpdate?: (progress: number) => void;
}

interface ModuleStatus {
  name: string;
  status: 'pending' | 'success' | 'failed';
  count?: number;
  duration?: number;
}

const TOTAL_MODULES = 5; // Autodoc, Oscaro, Mister Auto, Motointegrator, Distriauto

const SearchProgress = ({ oem, onComplete, onError, onProgressUpdate }: SearchProgressProps) => {
  const [progress, setProgress] = useState(0);
  const [statusText, setStatusText] = useState("Recherche en cours...");
  const [failedModules, setFailedModules] = useState<string[]>([]);
  const [accResults, setAccResults] = useState<any[]>([]);
  const [moduleStatuses, setModuleStatuses] = useState<ModuleStatus[]>([
    { name: 'Autodoc', status: 'pending' },
    { name: 'Oscaro', status: 'pending' },
    { name: 'Mister Auto', status: 'pending' },
    { name: 'Motointegrator', status: 'pending' },
    { name: 'Distriauto', status: 'pending' },
  ]);
  const evtSourceRef = useRef<EventSource | null>(null);

  useEffect(() => {
    debugLog('[SearchProgress] effect start', { oem });
    let modulesCompleted = 0;
    let allResults: any[] = [];
    let isClosing = false;
    let retryCount = 0;
    const MAX_RETRIES = 2;
    let retryTimeout: ReturnType<typeof setTimeout> | null = null;
    
    const startSearch = () => {
      debugLog('[SearchProgress] startSearch called', { oem });
      // Use Edge Function instead of Node.js backend
      const sseUrl = `https://vttviolbhwakxmptbqxn.supabase.co/functions/v1/search-oem?oem=${encodeURIComponent(oem)}`;
      debugLog('[SearchProgress] Starting SSE connection to:', sseUrl, `(attempt ${retryCount + 1}/${MAX_RETRIES + 1})`);
      
      const evtSource = new EventSource(sseUrl);
      evtSourceRef.current = evtSource;

      evtSource.onmessage = (event) => {
        if (isClosing) return; // Ignore events after closing
        
        try {
          const data = JSON.parse(event.data);
          debugLog('[SearchProgress] SSE event:', data);

          // Handle moduleDone events - aggregate results
          if (data.status === "moduleDone" && data.module) {
            const hasResults = Array.isArray(data.results) && data.results.length > 0;
            
            if (hasResults) {
              allResults = [...allResults, ...data.results];
              setAccResults(allResults);
            }
            
            // Update module status
            setModuleStatuses(prev => prev.map(m => 
              m.name === data.module 
                ? { ...m, status: hasResults ? 'success' : 'failed', count: data.results?.length || 0, duration: data.duration }
                : m
            ));
            
            modulesCompleted++;
            const percent = Math.min(Math.round((modulesCompleted / TOTAL_MODULES) * 100), 100);
            setProgress(percent);
            onProgressUpdate?.(percent);
            setStatusText(`Recherche en cours... ${data.module} (${percent}%)`);
          }
          
          // Handle failed module
          else if (data.status === "failed" && data.module) {
            setFailedModules(prev => [...prev, data.module]);
            
            // Update module status
            setModuleStatuses(prev => prev.map(m => 
              m.name === data.module 
                ? { ...m, status: 'failed', duration: data.duration }
                : m
            ));
            
            modulesCompleted++;
            const percent = Math.min(Math.round((modulesCompleted / TOTAL_MODULES) * 100), 100);
            setProgress(percent);
            onProgressUpdate?.(percent);
          }
          
          // Handle metrics summary
          else if (data.status === "metrics" && data.metrics) {
            debugLog('%c📊 MÉTRIQUES DE SCRAPING', 'font-weight: bold; font-size: 14px; color: #2563eb');
            debugLog('%c━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━', 'color: #2563eb');
            
            data.metrics.platforms.forEach((p: any) => {
              debugLog(`%c${p.platform}: ${p.bytesFormatted}`, 'color: #64748b');
            });
            
            debugLog('%c━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━', 'color: #2563eb');
            debugLog(`%cTOTAL: ${data.metrics.total.bytesFormatted} scrappés (${data.metrics.total.platformsCount} plateformes)`, 
              'font-weight: bold; color: #2563eb');
            debugLog('%c━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━', 'color: #2563eb');
          }
          
          // Handle final completion
          else if (data.status === "completed") {
            isClosing = true;
            setProgress(100);
            onProgressUpdate?.(100);
            setStatusText("Recherche terminée !");
            evtSource.close();
            evtSourceRef.current = null;
            
            // Use results from completed event (for cache hits) or accumulated results
            const finalResults = data.results || allResults;
            setTimeout(() => {
              debugLog('[SearchProgress] Completing with results:', finalResults.length, data.cached ? '(from cache)' : '(scraped)');
              onComplete(finalResults);
            }, 300);
          }
        } catch (err) {
          // Silently ignore parsing errors
        }
      };

      evtSource.onerror = (err) => {
        if (isClosing) return; // Already closed intentionally
        
        evtSource.close();
        evtSourceRef.current = null;
        
        // Retry logic - only if we haven't received any results yet
        if (retryCount < MAX_RETRIES && allResults.length === 0) {
          retryCount++;
          setStatusText(`Reconnexion en cours... (tentative ${retryCount}/${MAX_RETRIES})`);
          retryTimeout = setTimeout(() => startSearch(), 1000);
        } else {
          isClosing = true;
          // Use accumulated results if available
          if (allResults.length > 0) {
            onComplete(allResults);
          } else {
            onError("Erreur de connexion à l'edge function. Veuillez réessayer.");
          }
        }
      };
    };
    
    startSearch();

    return () => {
      isClosing = true;
      if (retryTimeout) clearTimeout(retryTimeout);
      if (evtSourceRef.current) {
        evtSourceRef.current.close();
        evtSourceRef.current = null;
      }
    };
  }, [oem]);

  const handleCancel = () => {
    if (evtSourceRef.current) {
      evtSourceRef.current.close();
      evtSourceRef.current = null;
    }
    setStatusText("Recherche annulée");
    onError("Recherche annulée par l'utilisateur");
  };

  return (
    <div className="space-y-4 glass-effect p-6 rounded-lg animate-fade-in">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Loader2 className="h-5 w-5 text-primary animate-spin" />
          <p className="text-foreground font-medium">{statusText}</p>
        </div>
        <button
          onClick={handleCancel}
          className="px-4 py-2 text-sm font-medium text-destructive hover:text-destructive/80 transition-colors"
        >
          Annuler
        </button>
      </div>
      
      <Progress value={progress} className="h-2" />

      {/* Indicateur visuel des modules */}
      <div className="grid grid-cols-5 gap-2">
        {moduleStatuses.map((module) => (
          <div 
            key={module.name}
            className={cn(
              "text-center p-2 rounded-md text-xs transition-all",
              module.status === 'pending' && "bg-muted/50 text-muted-foreground",
              module.status === 'success' && "bg-green-500/10 text-green-600 border border-green-500/20",
              module.status === 'failed' && "bg-red-500/10 text-red-600 border border-red-500/20"
            )}
          >
            <div className="font-medium truncate">{module.name}</div>
            {module.status === 'success' && module.count !== undefined && (
              <div className="text-[10px] mt-0.5">{module.count} résultats</div>
            )}
            {module.status === 'failed' && (
              <div className="text-[10px] mt-0.5">Échec</div>
            )}
          </div>
        ))}
      </div>

      {failedModules.length > 0 && (
        <Alert className="glass-effect border-accent/50">
          <AlertDescription className="text-sm text-muted-foreground">
            Plateformes indisponibles : {failedModules.join(", ")}
          </AlertDescription>
        </Alert>
      )}
    </div>
  );
};

export default SearchProgress;
